import React, { useState } from 'react';
import { parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import type { Matcher } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatDateISO, formatDateSpanish } from '@/lib/utils';

interface DateFilterPickerProps {
  id: string;
  // Dates as YYYY-MM-DD, the format the backend filters expect
  value?: string;
  minDate?: string;
  maxDate?: string;
  onChange: (value?: string) => void;
}

// react-day-picker's dropdown labels are not covered by the date-fns locale
const DROPDOWN_LABELS = {
  labelMonthDropdown: () => 'Mes:',
  labelYearDropdown: () => 'Año:',
};

// Optional date filter: clicking the selected day again clears it
export const DateFilterPicker: React.FC<DateFilterPickerProps> = ({
  id,
  value,
  minDate,
  maxDate,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const disabled: Matcher[] = [
    ...(minDate ? [{ before: parseISO(minDate) }] : []),
    ...(maxDate ? [{ after: parseISO(maxDate) }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn('w-full justify-start text-left font-normal', !value && 'text-muted-foreground')}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? formatDateSpanish(value) : 'dd/mm/aaaa'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={es}
          selected={value ? parseISO(value) : undefined}
          defaultMonth={value ? parseISO(value) : undefined}
          onSelect={(date) => {
            onChange(date ? formatDateISO(date) : undefined);
            setOpen(false);
          }}
          disabled={disabled}
          labels={DROPDOWN_LABELS}
          captionLayout="dropdown"
          fromYear={2020}
          toYear={new Date().getFullYear() + 1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
